/**
 * MAX-POWER SWEEP — enumerate capacity UPWARD until something binds.
 *
 * This is the structural mirror of `sizeGenerationEquipmentBlockCount.ts`, which
 * enumerates downward to find the SMALLEST fleet that serves a load. Same
 * doctrine, stated in that file's header and honoured here: every candidate is an
 * EXACT `solveGenerationImpact` run on a real re-application of the envelope —
 * no bisection, no interpolation, no closed-form shortcut — and every rung
 * honours the scenario's own N-k criterion via `capacity.contingencyNetMW`.
 *
 * ═══ WHY THERE IS NO EARLY EXIT ═══
 *
 * The land test is a SHELF PACKER over a real polygon with rotation, aspect
 * ratio and scheme selection. It is NOT monotone in MW: a larger fleet can pack
 * better than a smaller one. So the loop evaluates every rung on the ladder and
 * publishes `landFitNonMonotonic` when a rung above the first land failure still
 * fits. The reported ceiling is defined as "the highest EVALUATED rung that
 * fits", at a published increment — explicitly not a supremum, and capacity
 * strictly between rungs is unverified.
 *
 * ═══ WHY THE HEADLINE IS NOT min(ceilings) ═══
 *
 * Taking the minimum of independently-derived per-constraint ceilings can select
 * a capacity that actually FAILS land, precisely because land is non-monotone.
 * The maximum is therefore the highest rung where every documented constraint
 * passes SIMULTANEOUSLY, verified by a real solve.
 *
 * ═══ THE LOAD LOOP IS CLOSED ═══
 *
 * In max-power mode the load is an OUTPUT, not an input. `dataCenterConsumption`
 * is proportional to `facilityLoadMW`, so leaving the scenario's entered load
 * pinned would score the water ceiling against a datacenter that doesn't exist.
 * Each rung therefore solves once to read its firm capacity, writes the load it
 * can actually serve back into BOTH `facilityLoadMW` and `criticalLoadMW`,
 * re-solves, and RE-VERIFIES the verdicts — the same round-then-re-verify
 * discipline `solveMinimum` uses on the sizing side.
 */

import type {
  GenerationImpactResult,
  GenerationImpactScenarioInput,
  GenerationLayoutConfiguration,
  GenerationMaxPowerConstraintCeiling,
  GenerationMaxPowerConstraintId,
  GenerationMaxPowerLadderRung,
} from '../../types/generationImpact';
import type { EngineeringVerdict } from '../../types/generationImpact';
import { solveGenerationImpact } from './solveGenerationImpact';
import { hashGenerationImpactPayload } from './integrity';
import {
  activeGenerationEquipmentSelection,
  resizeGenerationEquipmentFleet,
} from './equipmentSelectionState';
import {
  buildUniformCandidate,
  capacityOnlyOptimizationSource,
  physicalBlockCount,
  uniformFleetBasis,
} from './generationAlternatives';
import {
  deriveMaxPowerConstraintCeilings,
  maxPowerCeilingContext,
  smallestDocumentedCeilingSiteGrossMW,
} from './maxPowerCeilings';
import { buildGenerationParcelTestFit } from './generationLayoutGeometry';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import { summarizeLayoutYield } from './generationLayoutYield';
import { searchGenerationLayouts } from './generationLayoutSearch';
import {
  GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE,
  isGridImportOnlyScenario,
} from './generationSizingValidation';

export type MaxPowerAxis = 'equipment-block-count' | 'uniform-block-rating';

/** Default reserve applied when the scenario leaves it unset (mirrors the solver). */
const DEFAULT_RESERVE_MARGIN_PCT = 15;
/** Ladder bounds — mirror the granularity ladder and the block-count sizer. */
const DEFAULT_MIN_BLOCK_COUNT = 1;
const DEFAULT_MAX_BLOCK_COUNT = 24;
const HARD_MAX_BLOCK_COUNT = 64;
const DEFAULT_INCREMENT_MW = 10;
const DEFAULT_MAX_ISO_GROSS_MW_PER_UNIT = 10_000;
/** Matches the layout search's evaluation budget. */
const DEFAULT_MAX_EVALUATIONS = 240;
/** Served-load resolution; the loop steps down by this on a float boundary. */
const LOAD_STEP_MW = 0.1;

export interface SweepMaxGenerationCapacityInput {
  input: GenerationImpactScenarioInput;
  /** Absent ⇒ no land ceiling; the study reports land as INDETERMINATE, never
   *  as "fits" (a synthetic envelope is sized from the footprint itself and
   *  would always fit — a self-fulfilling ceiling). */
  parcels?: readonly SiteBoundaryParcel[];
  layout?: GenerationLayoutConfiguration;
  fallbackAnchor?: { lat: number; lng: number };
  fallbackEnvelopeAcres?: number;
}

export interface SweepMaxGenerationCapacityOptions {
  /**
   * Search geometry-neutral placements when the current layout fails the land
   * gate. Default true — without it a land "ceiling" only means the plant did
   * not fit WHERE IT HAPPENED TO SIT.
   */
  optimisePlacement?: boolean;
  minBlockCount?: number;
  maxBlockCount?: number;
  incrementMW?: number;
  maxIsoGrossMWPerUnit?: number;
  maxEvaluations?: number;
}

export interface SweepMaxGenerationCapacityResult {
  status: 'available' | 'unavailable';
  reason: string | null;
  axis: MaxPowerAxis;
  axisLabel: string;
  fixedBlockCount: number | null;
  ladderIncrementMW: number | null;
  incrementCoarsenedFromMW: number | null;
  reachedSearchCeiling: boolean;
  landFitNonMonotonic: boolean;
  landBasis: 'parcel-geometry' | 'unavailable';
  rungs: GenerationMaxPowerLadderRung[];
  ceilings: GenerationMaxPowerConstraintCeiling[];
  warnings: string[];
}

function unavailable(reason: string, axis: MaxPowerAxis): SweepMaxGenerationCapacityResult {
  return {
    status: 'unavailable', reason, axis,
    axisLabel: axis === 'equipment-block-count' ? 'Equipment block count' : 'ISO gross MW per block',
    fixedBlockCount: null, ladderIncrementMW: null, incrementCoarsenedFromMW: null,
    reachedSearchCeiling: false, landFitNonMonotonic: false, landBasis: 'unavailable',
    rungs: [], ceilings: [], warnings: [],
  };
}

/** Site-rated gross MW across the fleet — the basis every intensity uses. */
function siteGrossMW(result: GenerationImpactResult): number {
  return result.unitResults.reduce((sum, r) => sum + r.siteGrossMWPerUnit * r.count, 0);
}

/**
 * Close the load loop for one candidate.
 *
 * Returns the re-solved input/result whose entered load equals the load the
 * fleet can actually serve, or null when the verdicts refuse to settle (which
 * the caller records as an INDETERMINATE rung rather than aborting the sweep).
 */
function closeLoadLoop(
  candidate: GenerationImpactScenarioInput,
  reserveMarginPct: number,
): { input: GenerationImpactScenarioInput; result: GenerationImpactResult } | null {
  const probe = solveGenerationImpact(candidate);
  if (probe.gaps.length > 0) return null;

  const firmNetMW = probe.capacity.contingencyNetMW;
  if (!Number.isFinite(firmNetMW) || firmNetMW <= 0) return null;

  let servable = Math.floor((firmNetMW / (1 + reserveMarginPct / 100)) / LOAD_STEP_MW) * LOAD_STEP_MW;

  // Round-then-RE-VERIFY, twice at most: the first pass can land exactly on a
  // float boundary where the reserve verdict flips.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (servable <= 0) return null;
    const settled: GenerationImpactScenarioInput = {
      ...candidate,
      facilityLoadMW: servable,
      criticalLoadMW: servable,
    };
    const result = solveGenerationImpact(settled);
    if (result.gaps.length === 0
      && result.capacity.contingencyVerdict === 'pass'
      && result.capacity.contingencyReserveVerdict === 'pass') {
      return { input: settled, result };
    }
    servable -= LOAD_STEP_MW;
  }
  return null;
}

/** Build one candidate on the chosen axis. Always via a sanctioned write path. */
function buildCandidate(
  axis: MaxPowerAxis,
  source: GenerationImpactScenarioInput,
  uniform: ReturnType<typeof uniformFleetBasis>['basis'],
  fixedBlockCount: number,
  rungValue: number,
): GenerationImpactScenarioInput {
  if (axis === 'equipment-block-count') {
    // Re-applies the equipment envelope, so `appliedDutyFingerprint` is
    // recomputed by the one owning code path. A hand-built units[] would be
    // rejected server-side by validateGenerationProjectDutyBasis.
    return resizeGenerationEquipmentFleet(source, rungValue);
  }
  return buildUniformCandidate(source, uniform!, fixedBlockCount, rungValue, source.ccs.enabled);
}

/** Score one rung against the documented constraints. */
function rungVerdicts(
  input: GenerationImpactScenarioInput,
  result: GenerationImpactResult,
  landFit: GenerationMaxPowerLadderRung['landFit'] | undefined,
): Array<{ id: GenerationMaxPowerConstraintId; verdict: EngineeringVerdict }> {
  const verdicts: Array<{ id: GenerationMaxPowerConstraintId; verdict: EngineeringVerdict }> = [];

  const gasLimit = input.availableGasCapacityDthPerDay;
  verdicts.push({
    id: 'gas-firm-capacity',
    verdict: gasLimit == null
      ? 'indeterminate'
      : result.gas.peakDthPerDay <= gasLimit ? 'pass' : 'fail',
  });

  const waterLimit = input.availableWaterConsumptionGalPerDay;
  const totalConsumption = result.water.totalConsumptionGalPerDay;
  verdicts.push({
    id: 'water-consumption',
    verdict: waterLimit == null || totalConsumption == null
      ? 'indeterminate'
      : totalConsumption <= waterLimit ? 'pass' : 'fail',
  });

  const co2Limit = input.ccsExportCapacityTonnesPerYear;
  verdicts.push({
    id: 'co2-export',
    verdict: !input.ccs.enabled
      ? 'pass' // not-applicable never blocks the ladder
      : co2Limit == null || !input.ccs.pipelineExportConfirmed
        ? 'indeterminate'
        : result.emissions.capturedCo2TonnesPerYear <= co2Limit ? 'pass' : 'fail',
  });

  // Receptor noise — the solver already scored each receptor against its own
  // documented threshold; the worst verdict governs.
  const scored = result.noise.filter(r => r.thresholdDbA != null);
  verdicts.push({
    id: 'receptor-noise',
    verdict: scored.length === 0
      ? 'indeterminate'
      : scored.some(r => r.verdict === 'fail') ? 'fail'
        : scored.some(r => r.verdict === 'indeterminate') ? 'indeterminate' : 'pass',
  });

  verdicts.push({
    id: 'land-parcel-fit',
    // Fail CLOSED: no parcel, or a clipping failure, is INDETERMINATE — never
    // an implicit pass, and never a genuine land ceiling.
    verdict: landFit == null || landFit.screeningUnavailable || landFit.status === 'indeterminate'
      ? 'indeterminate'
      : landFit.status === 'fits' && landFit.blocksContained === landFit.blocksPlaced ? 'pass' : 'fail',
  });

  return verdicts;
}

/**
 * PLACEMENT SEARCH SPACE — deliberately limited to geometry-NEUTRAL parameters.
 *
 * Rotation and block aspect ratio are free design choices: changing them moves
 * the plant, it does not weaken it. The other three search parameters are NOT
 * included, and that omission is a safety decision rather than a performance one:
 *
 *   edgeSetbackM   — a boundary setback, typically code- or ordinance-driven
 *   componentGapM  — equipment separation distance
 *   rowRoadWidthM  — operational / emergency access width
 *
 * Auto-shrinking any of those to force a fit would produce a layout that "fits"
 * only because the study quietly relaxed a requirement. The study finds a
 * PLACEMENT; it never negotiates a REQUIREMENT.
 */
const PLACEMENT_SEARCH_SPACE = {
  rotationDeg: { min: 0, max: 165, step: 15 },              // 12 orientations
  generationBlockAspectRatio: { min: 1, max: 3, step: 0.5 }, // 5 proportions
} as const;
/** 12 × 5 = 60 placements per search — bounded, and only spent when needed. */
const PLACEMENT_SEARCH_MAX_EVALUATIONS = 60;

/** Did this test-fit pass the land gate outright? */
function landFitPasses(testFit: { status: string }, summary: { blocksPlaced: number; blocksContained: number }): boolean {
  return testFit.status === 'fits' && summary.blocksContained === summary.blocksPlaced;
}

/** Evaluate the land fit for one rung. Degrades to undefined, never throws. */
function evaluateLandFit(
  args: SweepMaxGenerationCapacityInput,
  result: GenerationImpactResult,
  blockCount: number,
  /** Best-known placement carried forward from a lower rung; tried before searching. */
  seedLayout: GenerationLayoutConfiguration | undefined,
  optimisePlacement: boolean,
): GenerationMaxPowerLadderRung['landFit'] | undefined {
  if (!args.parcels || args.parcels.length === 0) return undefined;
  try {
    const base = {
      parcels: args.parcels,
      fallbackEnvelopeAcres: args.fallbackEnvelopeAcres,
      generationBlockCount: blockCount,
      footprint: result.footprint,
      fallbackAnchor: args.fallbackAnchor ?? { lat: 0, lng: 0 },
    };

    // ESCALATION, cheapest first.
    //  1. the placement carried forward from the last rung that fitted
    //  2. the scenario's own layout
    //  3. a bounded search over geometry-neutral parameters
    // Most sweeps never reach (3): once a placement works it keeps working as
    // capacity steps, so the search is typically spent once for the whole ladder.
    const attempts: Array<{ layout: GenerationLayoutConfiguration | undefined; source: 'carried-forward' | 'scenario-layout' }> = [];
    if (seedLayout) attempts.push({ layout: seedLayout, source: 'carried-forward' });
    attempts.push({ layout: args.layout, source: 'scenario-layout' });

    let testFit = buildGenerationParcelTestFit({ ...base, layout: attempts[0].layout });
    let summary = summarizeLayoutYield(testFit);
    let placementSource: 'carried-forward' | 'scenario-layout' | 'optimised' | 'exhausted' = attempts[0].source;
    let chosenLayout = attempts[0].layout;
    let placementsEvaluated = 1;

    for (let i = 1; i < attempts.length && !landFitPasses(testFit, summary); i += 1) {
      const candidate = buildGenerationParcelTestFit({ ...base, layout: attempts[i].layout });
      const candidateSummary = summarizeLayoutYield(candidate);
      placementsEvaluated += 1;
      if (landFitPasses(candidate, candidateSummary) || candidate.status !== 'indeterminate') {
        testFit = candidate;
        summary = candidateSummary;
        placementSource = attempts[i].source;
        chosenLayout = attempts[i].layout;
      }
      if (landFitPasses(testFit, summary)) break;
    }

    // (3) Only now spend the search — and only when land is the thing failing.
    let resolvedPlacement: Record<string, number> | undefined;
    if (optimisePlacement && !landFitPasses(testFit, summary) && testFit.status !== 'indeterminate') {
      const search = searchGenerationLayouts({
        base,
        baseLayout: { ...(chosenLayout ?? args.layout ?? {}), schemaVersion: 2 } as GenerationLayoutConfiguration,
        space: PLACEMENT_SEARCH_SPACE as never,
        topN: 1,
        maxEvaluations: PLACEMENT_SEARCH_MAX_EVALUATIONS,
      });
      placementsEvaluated += search.evaluatedCount;
      const best = search.candidates[0];
      if (best && landFitPasses(best.testFit, best.yieldSummary)) {
        testFit = best.testFit;
        summary = best.yieldSummary;
        chosenLayout = best.layout;
        placementSource = 'optimised';
        resolvedPlacement = best.resolvedValues as Record<string, number>;
      } else {
        // Every placement in the bounded space was tried and none fitted. THAT
        // is a real land ceiling — not "it did not fit where it happened to sit".
        placementSource = 'exhausted';
      }
    }

    // `status: 'partial'` is a CATCH-ALL. It covers a genuine geometric failure
    // (the packer overflowed, geometry spills outside the polygon, a component
    // sits inside the setback) AND the case where a screen simply could not be
    // COMPUTED. Those must not collapse into the same verdict: reporting
    // "the parcel is too small" when the truth is "we could not measure the
    // clearance" invents an engineering finding out of a missing input.
    //
    // Each of these is an UNKNOWN, never a failure:
    //   - setback clearance null  ⇒ no component clearance could be derived
    //   - exclusion overlap null  ⇒ polygon clipping FAILED (≠ no exclusions)
    //   - obstacle overlap null   ⇒ collision analysis failed
    const exclusionsActive = (args.layout?.exclusions?.zones?.length ?? 0) > 0;
    const setbackUnknown = testFit.parcelEdgeSetbackSatisfied == null;
    const exclusionScreenFailed = exclusionsActive && testFit.exclusionOverlapAcres == null;
    const obstacleScreenFailed = testFit.obstacleOverlapAcres === null;
    const screeningUnavailable =
      testFit.status === 'indeterminate'
      || setbackUnknown
      || exclusionScreenFailed
      || obstacleScreenFailed;
    const screeningUnavailableReason = testFit.status === 'indeterminate'
      ? 'the parcel could not be resolved'
      : setbackUnknown ? 'the parcel-edge clearance could not be computed'
        : exclusionScreenFailed ? 'exclusion-zone clipping failed'
          : obstacleScreenFailed ? 'obstacle collision analysis failed'
            : null;

    return {
      status: testFit.status,
      blocksPlaced: summary.blocksPlaced,
      blocksContained: summary.blocksContained,
      containmentPct: testFit.containmentPct ?? null,
      outsideAcres: testFit.outsideAcres ?? null,
      areaUtilizationPct: summary.areaUtilizationPct ?? null,
      greenReservePct: summary.greenReservePct ?? null,
      usableAcres: summary.parcelAcres ?? null,
      totalFootprintAcres: result.footprint.totalAcres,
      screeningUnavailable,
      ...(screeningUnavailableReason ? { screeningUnavailableReason } : {}),
      placementSource,
      placementsEvaluated,
      ...(resolvedPlacement ? { optimisedPlacement: resolvedPlacement } : {}),
      ...(chosenLayout ? { resolvedLayout: chosenLayout } : {}),
    };
  } catch {
    // Per-rung failure degrades to indeterminate — the fail-soft convention
    // `rankAlternativeParcelFits` already uses. A thrown rung must never abort
    // the whole study.
    return {
      status: 'indeterminate',
      blocksPlaced: 0, blocksContained: 0,
      containmentPct: null, outsideAcres: null,
      areaUtilizationPct: null, greenReservePct: null, usableAcres: null,
      totalFootprintAcres: result.footprint.totalAcres,
      screeningUnavailable: true,
      screeningUnavailableReason: 'the parcel test-fit threw',
      placementSource: 'scenario-layout',
      placementsEvaluated: 0,
    };
  }
}

export function sweepMaxGenerationCapacity(
  args: SweepMaxGenerationCapacityInput,
  options: SweepMaxGenerationCapacityOptions = {},
): SweepMaxGenerationCapacityResult {
  const source = args.input;
  if (isGridImportOnlyScenario(source)) {
    return unavailable(GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE, 'uniform-block-rating');
  }
  const selection = activeGenerationEquipmentSelection(source);
  const axis: MaxPowerAxis = selection ? 'equipment-block-count' : 'uniform-block-rating';
  const warnings: string[] = [];

  const reserveMarginDefaulted = source.reserveMarginPct == null;
  const reserveMarginPct = source.reserveMarginPct ?? DEFAULT_RESERVE_MARGIN_PCT;

  // A base solve anchors every analytic ceiling and the linearity assumption.
  const baseResult = solveGenerationImpact(source);
  if (baseResult.gaps.length > 0) {
    return unavailable(`The scenario is indeterminate: ${baseResult.gaps.join(' ')}`, axis);
  }

  // MIXED FLEETS ARE REFUSED. Every analytic ceiling assumes consumption is
  // linear in site-gross MW at a fixed shape — true for a uniform fleet, false
  // when the swept axis changes the technology mix.
  const uniform = uniformFleetBasis(source);
  if (!uniform.basis && axis === 'uniform-block-rating') {
    return unavailable(
      `A max-power sweep requires a uniform fleet basis: ${uniform.errors.join(' ')}`,
      axis,
    );
  }
  if (!uniform.basis && axis === 'equipment-block-count') {
    warnings.push(
      'The fleet is not uniform, so the analytic ceilings are indicative only; every rung is still verified by an exact solve.',
    );
  }

  const ctx = maxPowerCeilingContext(source, baseResult);
  const ceilings = [...deriveMaxPowerConstraintCeilings(ctx, axis)];
  const smallestCeiling = smallestDocumentedCeilingSiteGrossMW(ceilings);

  // ── Build the ladder ───────────────────────────────────────────────────────
  const maxEvaluations = options.maxEvaluations ?? DEFAULT_MAX_EVALUATIONS;
  let ladder: number[];
  let ladderIncrementMW: number | null = null;
  let incrementCoarsenedFromMW: number | null = null;
  let fixedBlockCount: number | null = null;

  if (axis === 'equipment-block-count') {
    const min = Math.max(1, options.minBlockCount ?? DEFAULT_MIN_BLOCK_COUNT);
    const max = Math.min(options.maxBlockCount ?? DEFAULT_MAX_BLOCK_COUNT, HARD_MAX_BLOCK_COUNT);
    ladder = [];
    for (let k = min; k <= max && ladder.length < maxEvaluations; k += 1) ladder.push(k);
  } else {
    fixedBlockCount = physicalBlockCount(source);
    let increment = options.incrementMW ?? DEFAULT_INCREMENT_MW;
    const hardMax = options.maxIsoGrossMWPerUnit ?? DEFAULT_MAX_ISO_GROSS_MW_PER_UNIT;

    // Bracket at the smallest DOCUMENTED ceiling, rounded UP one increment so
    // the first failing rung is always inside the ladder. With nothing
    // documented we run to the search ceiling and say so.
    const perBlockCeiling = smallestCeiling != null && fixedBlockCount > 0
      ? (smallestCeiling / fixedBlockCount) * (baseResult.unitResults[0]
        ? source.units[0].isoGrossMWPerUnit / baseResult.unitResults[0].siteGrossMWPerUnit
        : 1)
      : null;
    const bracket = Math.min(
      perBlockCeiling != null && Number.isFinite(perBlockCeiling)
        ? Math.ceil(perBlockCeiling / increment) * increment + increment
        : hardMax,
      hardMax,
    );

    // COARSEN rather than truncate. Truncation would report a false ceiling;
    // a coarser ladder reports a true one at lower resolution.
    const originalIncrement = increment;
    while (bracket / increment > maxEvaluations) increment *= 2;
    if (increment !== originalIncrement) incrementCoarsenedFromMW = originalIncrement;
    ladderIncrementMW = increment;

    ladder = [];
    for (let mw = increment; mw <= bracket && ladder.length < maxEvaluations; mw += increment) {
      ladder.push(mw);
    }
  }

  if (ladder.length === 0) return unavailable('The capacity ladder is empty.', axis);

  // ── Evaluate EVERY rung. No break. ─────────────────────────────────────────
  const optimizationSource = axis === 'uniform-block-rating'
    ? capacityOnlyOptimizationSource(source)
    : source;

  const rungs: GenerationMaxPowerLadderRung[] = [];
  // The placement that last worked. Carried forward so the bounded search is
  // typically spent ONCE for the whole ladder rather than per rung.
  let seedLayout: GenerationLayoutConfiguration | undefined;
  const optimisePlacement = options.optimisePlacement ?? true;
  ladder.forEach((rungValue, index) => {
    let candidate: GenerationImpactScenarioInput;
    try {
      candidate = buildCandidate(axis, optimizationSource, uniform.basis, fixedBlockCount ?? 1, rungValue);
    } catch {
      return; // this rung is not constructible; skip without aborting
    }

    const settled = closeLoadLoop(candidate, reserveMarginPct);
    if (!settled) return;

    const { input: rungInput, result } = settled;
    const blockCount = axis === 'equipment-block-count' ? rungValue : (fixedBlockCount ?? 1);
    const landFit = evaluateLandFit(args, result, blockCount, seedLayout, optimisePlacement);
    // Only carry forward a placement that actually WORKED.
    if (landFit?.resolvedLayout
      && landFit.status === 'fits'
      && landFit.blocksContained === landFit.blocksPlaced) {
      seedLayout = landFit.resolvedLayout;
    }
    const verdicts = rungVerdicts(rungInput, result, landFit);

    rungs.push({
      index,
      blockCount,
      isoGrossMWPerBlock: rungInput.units[0]?.isoGrossMWPerUnit ?? 0,
      isoGrossNameplateMW: rungInput.units.reduce((s, u) => s + u.isoGrossMWPerUnit * u.count, 0),
      siteGrossMW: siteGrossMW(result),
      normalNetMW: result.capacity.normalNetMW,
      firmNetMW: result.capacity.contingencyNetMW,
      servableCriticalLoadMW: rungInput.facilityLoadMW,
      constraintVerdicts: verdicts,
      // A rung is feasible when nothing DOCUMENTED fails. Indeterminate does
      // not block — it downgrades the study's boundQuality instead.
      feasible: verdicts.every(v => v.verdict !== 'fail'),
      landFit,
      inputHash: hashGenerationImpactPayload(rungInput),
      resultHash: hashGenerationImpactPayload(result),
    });
  });

  if (rungs.length === 0) {
    return unavailable('No capacity rung could be evaluated for this scenario.', axis);
  }

  // ── Land monotonicity + per-constraint last-passing rung ───────────────────
  const firstLandFailure = rungs.findIndex(r =>
    r.constraintVerdicts.find(v => v.id === 'land-parcel-fit')?.verdict === 'fail');
  const landFitNonMonotonic = firstLandFailure >= 0 && rungs.slice(firstLandFailure + 1).some(r =>
    r.constraintVerdicts.find(v => v.id === 'land-parcel-fit')?.verdict === 'pass');
  if (landFitNonMonotonic) {
    warnings.push(
      'The parcel packer is NOT monotone in capacity: a larger fleet packed successfully above a smaller one that did not.'
      + ' The reported ceiling is the largest VERIFIED fit on the evaluated ladder, not a supremum.',
    );
  }

  for (const ceiling of ceilings) {
    const last = [...rungs].reverse().find(r =>
      r.constraintVerdicts.find(v => v.id === ceiling.id)?.verdict === 'pass');
    ceiling.lastPassingRungIndex = last?.index ?? null;
  }

  const lastRung = rungs[rungs.length - 1];
  const reachedSearchCeiling = lastRung.feasible;
  if (reachedSearchCeiling) {
    warnings.push(
      'Every evaluated rung satisfied the documented constraints, so the search ceiling — not a physical limit — bounded this study.'
      + ' Read the maximum as "at least", never as an equality.',
    );
  }

  return {
    status: 'available',
    reason: null,
    axis,
    axisLabel: axis === 'equipment-block-count' ? 'Equipment block count' : 'ISO gross MW per block',
    fixedBlockCount,
    ladderIncrementMW,
    incrementCoarsenedFromMW,
    reachedSearchCeiling,
    landFitNonMonotonic,
    landBasis: args.parcels && args.parcels.length > 0 ? 'parcel-geometry' : 'unavailable',
    rungs,
    ceilings,
    warnings: [
      ...warnings,
      ...(incrementCoarsenedFromMW != null
        ? [`The ladder increment was coarsened from ${incrementCoarsenedFromMW} MW to ${ladderIncrementMW} MW to fit the evaluation budget; the ceiling is true but lower-resolution.`]
        : []),
      ...(reserveMarginDefaulted
        ? [`No planning reserve was declared, so ${DEFAULT_RESERVE_MARGIN_PCT}% was applied — this reduces the servable load by roughly 13%.`]
        : []),
      ...(args.parcels && args.parcels.length > 0
        ? [
          // The land ceiling is LAYOUT-CONDITIONAL. Every rung is packed at the
          // scenario's CURRENT rotation / setback / gap / anchor; the study does
          // not re-optimise placement per capacity (that would multiply the
          // evaluation count by the whole parameter grid). So a parcel far
          // larger than the plant can still fail on placement alone — which is a
          // Studio adjustment, not a land shortfall.
          optimisePlacement
            ? 'The land screen AUTO-OPTIMISES placement: when the current layout fails, rotation and block aspect ratio are searched for a placement that fits. Setbacks, component gaps and road widths are NEVER relaxed — they are requirements, not free variables — so a land failure means no compliant placement was found, not that a spacing rule was in the way.'
            : 'Placement optimisation is DISABLED, so the land screen only asks whether the plant fits where it currently sits. A parcel much larger than the plant can fail on placement alone; do not read this land result as an area limit.',
        ]
        : ['No parcel is available, so the LAND ceiling is indeterminate. A synthetic envelope is sized from the footprint itself and would always "fit", so it is deliberately not used here.']),
      ...(source.footprint.coolingAcresPerGrossMW == null
        ? ['Cooling and switchyard area were held CONSTANT across the sweep (no per-MW intensity is declared), so the land ceiling is OPTIMISTIC at capacities well above the scenario\'s current rating.']
        : ['Cooling area scales by a per-MW intensity — a first-order linearisation with no wet-bulb dependence and no train-count steps.']),
      'The documented constraint set is INCOMPLETE: it excludes air-permit limits (NOx/CO are calculated but no site limit is modelled), water WITHDRAWAL, thermal discharge, electrical interconnection capacity, and construction access/laydown.',
    ],
  };
}
